/**
 * usePlayerLog — diagnostic event logger for the learning player.
 *
 * Buffers events in memory, flushes batches to /api/player-events
 * every FLUSH_INTERVAL_MS or when the buffer crosses BATCH_TRIGGER. On
 * page hide / unmount, sends a final beacon to avoid losing the last
 * few seconds of events.
 *
 * Failures are silent — diagnostic logging must never block the user
 * experience. If the network is unreachable the batch is PERSISTED to a small
 * bounded on-device queue (composables/playerEventQueue.ts) and replayed, in
 * order and with its original occurred_at timestamps, on the next flush, the
 * next `online` event or the next page load. It used to be dropped outright;
 * that stopped being acceptable when jobs #655/#657 put Script mode's
 * adaptation signal on this rail, so an offline session taught the population
 * nothing (Tom, via RBF, 2026-09-22 13:15Z). The queue is bounded and drops
 * OLDEST past its cap — see playerEventQueue.ts for the cap and its reasoning.
 *
 * Usage:
 *   const log = usePlayerLog({ courseCode })
 *   log.event('tap_skip', { during: 'pod_lap', round: 42 })
 */

import { onMounted, onBeforeUnmount, type Ref } from 'vue'
import { isOfflineish } from '../config/networkGate'
import { useUserRole } from '@/composables/useUserRole'
import { apiUrl } from '@/platform/apiBase'
import { platform } from '@/platform/capabilities'
import {
  enqueueBatch,
  removeBatch,
  peekQueue,
  peekDroppedCount,
  clearDropped,
  type QueuedBatch,
  type QueuedEvent,
} from './playerEventQueue'

interface PlayerEvent {
  /**
   * IDEMPOTENCE KEY, stamped once at the instant the event is created and
   * never regenerated. player_events had no dedupe key of its own, so job #666
   * added a nullable `client_event_id uuid` with a plain unique index and the
   * endpoint inserts ON CONFLICT DO NOTHING. It matters because a batch can be
   * sent and not acked — a keepalive fetch on unload never reports back, and a
   * response lost on a flaky link looks identical to a failure — so without it
   * every replay risks double-inserting. Nulls never conflict in Postgres, so
   * an older client that sends none is wholly unaffected.
   */
  client_event_id: string
  event_type: string
  payload?: Record<string, unknown> | null
  course_code?: string | null
  session_id?: string
  occurred_at: string
  client_version?: string | null
  /**
   * WHO THIS EVENT BELONGS TO, resolved at the instant it was RECORDED and
   * never again (job #671). #666 asked `resolveLearnerId()` at flush time, so
   * events recorded as learner A and flushed after a switch to learner B were
   * filed — and could be replayed — under B. Stripped before the event goes on
   * the wire; it is a routing fact about the batch, not a column.
   */
  recordedLearnerId?: string | null
}

/**
 * What a play-state context can stamp. Every field optional: a surface that has
 * no belt (Listening Mode) leaves it out rather than inventing one.
 */
export interface PlayerLogContext {
  /** The learning mode in force: 'easy' | 'fast' in the player, 'listening' in Listening Mode. */
  mode?: string | null
  /** Belt name the learner is PLAYING under (playingBelt), not the belt achieved. */
  belt?: string | null
  seedId?: string | null
  roundIndex?: number | null
  [key: string]: unknown
}

interface PlayerLogOptions {
  /** Reactive course code — stamped on every event. Optional; can be unresolved at session start. */
  courseCode?: Ref<string | null | undefined> | string | null
  /** Reactive learner id (incl. `guest-<uuid>`) — merged into every event's
   *  payload so GUEST sessions are attributable. player_events has no
   *  learner_id column and user_id is null for guests, so without this a
   *  guest's runs are untrackable across sessions. */
  learnerId?: Ref<string | null | undefined> | string | null
  /**
   * Play-as-class audit trail (owner ruling 2026-07-16): while a class-mode
   * session is active, `learnerId` above resolves to the CLASS's own learner
   * id (the DB row every event attributes to, via the ssi-user-id cookie) —
   * this carries the DRIVING STAFF MEMBER's auth uid alongside it in every
   * event's payload, so "which teacher was actually at the keyboard" is
   * never lost even though the telemetry itself belongs to the class.
   */
  actorUserId?: Ref<string | null | undefined> | string | null
  /** Bundle hash / git sha to stamp for triage. */
  clientVersion?: string
  /** Override flush interval (ms). */
  flushIntervalMs?: number
  /**
   * Supabase access token getter (SEC25 INPUT-04). /api/player-events no
   * longer trusts the `ssi-user-id` cookie as an identity — a signed-in
   * learner's events are attributed from a VERIFIED bearer, and anything
   * without one is stored unattributed (guest). Omit it and the log still
   * works; the rows just carry no learner id.
   */
  getToken?: () => Promise<string | null>
  /**
   * Play-state context, stamped on EVERY event this log emits (job #325, Tom
   * 2026-09-12: "belt (and seed/round context) stored on the row, not derived
   * from seedId"). Called at the instant each event is logged, so a row carries
   * the mode and belt IN FORCE at that play, not the page-load selection and
   * not a stored preference. Keys the caller already set on its payload, even
   * to null, are never overwritten: a pod play's deliberate `seedId: null`
   * stays null. A context that throws stamps nothing and the row goes out as
   * it always did.
   */
  context?: () => PlayerLogContext | null | undefined
}

const DEFAULT_FLUSH_INTERVAL_MS = 5000
const BATCH_TRIGGER = 10
const MAX_BUFFER = 200 // hard cap; events past this are dropped
// How long a HIDDEN-tab flush waits for a pending bearer before securing the
// batch as an unattributed beacon. Supabase's getSession() answers from local
// storage in single-digit ms; it takes longer only when the access token has
// expired and must be refreshed over the network, or the auth client's
// navigator lock is held by another tab. This is a bounded TRADE-OFF, not a
// close — see flush() for the floor it sits on.
const HIDDEN_TOKEN_WAIT_MS = 800
// A flush whose page is NOT at risk of eviction (a player unmount inside a
// live tab, or a tab that has come back to the foreground) keeps waiting for
// the bearer up to this much. It is a guard against a hung auth client only:
// past it the batch goes out unattributed rather than never.
const SAFE_TOKEN_WAIT_MS = 10_000

let nextSessionId: string | null = null

// Live player logs, for the learner bug-report postbox (job #327): the report
// flushes every buffer first so the server can read the last five minutes from
// player_events, and carries whatever is STILL unflushed in its own body as a
// fallback. Registered on mount, removed on unmount; nothing else reads this.
interface LiveLog { flush: () => Promise<void>; pending: () => PlayerEvent[] }
const liveLogs = new Set<LiveLog>()

/** Flush every mounted player log now. Silent on failure, like flush itself. */
export async function flushAllPlayerLogs(): Promise<void> {
  await Promise.all([...liveLogs].map((l) => l.flush().catch(() => {})))
}

/** Every event still buffered in a mounted player log, in arrival order. */
export function pendingPlayerEvents(): PlayerEvent[] {
  return [...liveLogs].flatMap((l) => l.pending())
}
/**
 * Which bearer a batch goes out with — or whether it goes out at all (job #504).
 *
 * `recorded` is who the batch was recorded under, `current` who this log
 * resolves as NOW, `token` the bearer this log holds, `hasTokenSource` whether
 * this log was given a getToken at all.
 *
 * THE LEAK THIS CLOSES. The on-device queue is ONE localStorage file shared by
 * every mounted log, and not every log knows who the learner is: the
 * sign-in modal's audit log (auth/loginCode.ts) is mounted for the whole of a
 * player session with no learnerId and no getToken. Under #671's rule its
 * 5-second tick found a learner's queued batch, saw "recorded ≠ current
 * (null)", and posted it with no bearer — so every batch a flaky connection
 * had queued was filed anonymous whenever that log's tick won the race. That
 * is the pattern production showed on 27-28 Sep: anonymous bursts beside
 * audio_failed rows, inserted out of occurred_at order, inside sessions whose
 * neighbouring rows were credited.
 *
 * The rule, in order:
 *   • nothing recorded, or a guest — send, as before;
 *   • a real learner, and this log cannot say who is signed in (current null)
 *     — HOLD: leave it for a log that can. Never drain a learner's batch
 *     anonymously just because this log does not know them;
 *   • a real learner who is not the one signed in now — send unattributed
 *     (#671: never attach somebody else's bearer);
 *   • the learner signed in now, but no bearer came back — HOLD rather than
 *     file their own events anonymous; the next flush will have one.
 */
export function bearerDecision(
  recorded: string | null | undefined,
  current: string | null,
  token: string | null,
  hasTokenSource: boolean,
): { hold: boolean; token: string | null } {
  if (!recorded) return { hold: false, token }
  if (recorded.startsWith('guest-')) return { hold: false, token: recorded === current ? token : null }
  if (!current) return { hold: true, token: null }
  if (recorded !== current) return { hold: false, token: null }
  if (hasTokenSource && !token) return { hold: true, token: null }
  return { hold: false, token }
}

function genSessionId(): string {
  // Prefer crypto.randomUUID where available (modern browsers).
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  // Fallback: timestamp + random
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

/**
 * A per-event idempotence key. crypto.randomUUID where available; the fallback
 * is only ever used by browsers that also predate it, and collides at a rate
 * far below the one duplicate row it would cost.
 */
function genEventId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  const h = (n: number) => Math.floor(Math.random() * 16 ** n).toString(16).padStart(n, '0')
  return `${h(8)}-${h(4)}-4${h(3)}-a${h(3)}-${h(12)}`
}

export function usePlayerLog(options: PlayerLogOptions = {}) {
  const sessionId = nextSessionId || (nextSessionId = genSessionId())
  const clientVersion = options.clientVersion ?? null
  const flushIntervalMs = options.flushIntervalMs ?? DEFAULT_FLUSH_INTERVAL_MS

  const buffer: PlayerEvent[] = []
  let flushTimer: ReturnType<typeof setInterval> | null = null

  // Last known access token, refreshed on every async flush so the unload
  // path (which cannot await) still has one to send.
  let cachedToken: string | null = null
  // The refresh currently in flight, if any. A sync flush that finds the
  // cache empty while this is pending WAITS for it (bounded) rather than
  // beaconing without a bearer — see flush().
  let tokenInFlight: Promise<void> | null = null
  const refreshToken = (): Promise<void> => {
    if (!options.getToken) return Promise.resolve()
    if (tokenInFlight) return tokenInFlight
    const p = (async () => {
      try {
        cachedToken = await options.getToken!()
      } catch { /* silent — telemetry never blocks UX */ }
    })().finally(() => { if (tokenInFlight === p) tokenInFlight = null })
    tokenInFlight = p
    return p
  }

  const resolveCourseCode = (): string | null => {
    const v = options.courseCode
    if (!v) return null
    if (typeof v === 'string') return v
    if (typeof v === 'object' && 'value' in v) return (v.value as string) ?? null
    return null
  }

  const resolveLearnerId = (): string | null => {
    const v = options.learnerId
    if (!v) return null
    if (typeof v === 'string') return v
    if (typeof v === 'object' && 'value' in v) return (v.value as string) ?? null
    return null
  }

  const resolveActorUserId = (): string | null => {
    const v = options.actorUserId
    if (!v) return null
    if (typeof v === 'string') return v
    if (typeof v === 'object' && 'value' in v) return (v.value as string) ?? null
    return null
  }

  /**
   * Log a player event. Type is a short snake_case label; payload is
   * arbitrary structured context. Stamped with course + session +
   * timestamp; user / device / IP are added server-side.
   */
  const event = (type: string, payload?: Record<string, unknown>): void => {
    if (typeof type !== 'string' || type.length === 0) return
    if (buffer.length >= MAX_BUFFER) return // drop, never grow unbounded
    // View-as: refused at CREATION, not only at flush. The flush guard below
    // reads the flag when the batch leaves, so an event queued while
    // viewing-as and flushed after the admin exited view-as went out under
    // the admin's own learner (Astra cold-check #606 on the #602 fix). An
    // event born under view-as is never a learner's event, so it is never
    // buffered; the flush guard and the server 403 stay as belt and braces.
    if (useUserRole().isViewingAs.value) return

    const learnerId = resolveLearnerId()
    const actorUserId = resolveActorUserId()
    const extra = {
      ...(learnerId ? { learnerId } : {}),
      ...(actorUserId ? { actor_user_id: actorUserId } : {}),
    }
    // Context keys fill only what the caller left ABSENT. `'k' in payload` is
    // the test, so an explicit null from the caller wins over the context.
    let context: PlayerLogContext | null | undefined
    try { context = options.context?.() } catch { context = null }
    const stamped: Record<string, unknown> = {}
    if (context) {
      for (const [k, v] of Object.entries(context)) {
        if (v === undefined) continue
        if (payload && k in payload) continue
        stamped[k] = v
      }
    }
    const hasExtra = Object.keys(extra).length > 0 || Object.keys(stamped).length > 0
    buffer.push({
      client_event_id: genEventId(),
      recordedLearnerId: learnerId,
      event_type: type,
      payload: hasExtra ? { ...stamped, ...(payload ?? {}), ...extra } : (payload ?? null),
      course_code: resolveCourseCode(),
      session_id: sessionId,
      occurred_at: new Date().toISOString(),
      client_version: clientVersion,
    })

    if (buffer.length >= BATCH_TRIGGER) {
      void flush()
    }
  }

  /** 'web' | 'webview' — asked once per batch, as the wire body wants it. */
  const shellNow = (): string => {
    try { return platform().shell } catch { return 'web' }
  }

  const bodyFor = (batch: QueuedBatch): string => JSON.stringify({
    events: batch.events,
    app_shell: batch.appShell ?? shellNow(),
    ...(batch.actingLearnerId && !batch.actingLearnerId.startsWith('guest-')
      ? { acting_learner_id: batch.actingLearnerId }
      : {}),
  })

  /**
   * Split a buffer into runs of ONE identity, in arrival order, stripping the
   * internal `recordedLearnerId` off each event on the way out — it is a fact
   * about the batch, not a column on the row. A buffer that spans a learner
   * switch becomes two batches, each replayable under its own identity and
   * neither able to borrow the other's bearer.
   */
  const segmentsOf = (events: PlayerEvent[]): Array<{ recordedLearnerId: string | null; events: QueuedEvent[] }> => {
    const out: Array<{ recordedLearnerId: string | null; events: QueuedEvent[] }> = []
    for (const e of events) {
      const { recordedLearnerId = null, ...wire } = e
      const last = out[out.length - 1]
      if (last && last.recordedLearnerId === recordedLearnerId) last.events.push(wire)
      else out.push({ recordedLearnerId, events: [wire] })
    }
    return out
  }

  /** Hand one single-identity segment to the on-device queue. */
  const persistUnsent = (seg: { recordedLearnerId: string | null; events: QueuedEvent[] }): void => {
    if (seg.events.length === 0) return
    enqueueBatch({
      events: seg.events,
      appShell: shellNow(),
      actingLearnerId: seg.recordedLearnerId,
      recordedLearnerId: seg.recordedLearnerId,
    })
  }

  const decide = (recorded: string | null | undefined, current: string | null, token: string | null) =>
    bearerDecision(recorded, current, token, !!options.getToken)

  /**
   * POST one batch and say what should happen to it.
   *   ok           — accepted (or the response was opaque, which we take as
   *                  accepted; the client_event_id makes a wrong guess cost
   *                  nothing).
   *   retry        — the network or the server failed transiently: keep it.
   *   unauthorized — 401: the bearer had expired (job #504). Keep it; the
   *                  caller may refresh and re-send once.
   *   drop         — the server refused it on its merits (400 malformed, 403
   *                  view-as). Retrying that forever would only fill the queue.
   */
  const postBatch = async (body: string, token: string | null): Promise<'ok' | 'retry' | 'unauthorized' | 'drop'> => {
    try {
      const res = await fetch('/api/player-events', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body,
        keepalive: true, // best-effort survive unload even via fetch
      })
      // A test double or an opaque response may carry no status; treated as
      // accepted, which the client_event_id makes safe to be wrong about.
      const status = typeof res?.status === 'number' ? res.status : 200
      if (status >= 500 || status === 408 || status === 429) return 'retry'
      if (status === 401) return 'unauthorized'
      if (status >= 400) return 'drop'
      return 'ok'
    } catch {
      return 'retry' // silent — diagnostic logging never blocks UX
    }
  }

  /**
   * postBatch, plus ONE refresh-and-resend on a 401 (job #504). Before, the
   * server filed an expired bearer's batch anonymous and said 200; now it says
   * 401, and the batch is re-sent under a freshly fetched bearer — getSession
   * refreshes an expired access token. A second 401, or no fresher bearer to
   * try, keeps the batch: 'unauthorized' goes back to the caller, which queues
   * it. It is never dropped for a 401.
   */
  const sendBatch = async (body: string, token: string | null, mayRefresh: boolean): Promise<'ok' | 'retry' | 'unauthorized' | 'drop'> => {
    const outcome = await postBatch(body, token)
    if (outcome !== 'unauthorized' || !mayRefresh || !token) return outcome
    await refreshToken()
    const fresh = cachedToken
    if (!fresh || fresh === token) return 'unauthorized'
    return postBatch(body, fresh)
  }

  let replaying = false
  /**
   * Drain the on-device queue, OLDEST FIRST, one batch per request.
   *
   * RETAIN UNTIL ACKED (job #671). #666 called `takeQueue()`, which removed
   * every batch from localStorage before the first request was even made, then
   * awaited a token refresh and posted them one by one — so a page evicted or
   * reloaded mid-replay lost every batch that had not yet gone out, which is
   * precisely the loss this queue exists to prevent. Now each batch is PEEKED,
   * posted, and removed by its own id only once the post came back ok; a
   * transient failure stops the drain and leaves everything still queued,
   * untouched and in order. A re-send of an already-accepted batch costs
   * nothing: `client_event_id` makes the insert idempotent.
   *
   * Nothing here re-stamps occurred_at: a replayed row describes the moment it
   * happened.
   */
  const replayQueued = async (): Promise<void> => {
    if (replaying) return
    if (isOfflineish()) return
    if (useUserRole().isViewingAs.value) return
    replaying = true
    try {
      const queued = peekQueue()
      const currentLearnerId = resolveLearnerId()
      for (const b of queued) {
        // IDENTITY (spec 4). A queued batch replays under the identity it was
        // recorded with, and the only identity the server trusts is a verified
        // bearer. The token itself is deliberately not persisted — it expires
        // within the hour and a token at rest is a liability — so the bearer
        // is attached only when the learner signed in NOW is the same learner
        // the batch was recorded under. Anything else (signed out since, a
        // different account on the same device, a guest recording) goes out
        // with no bearer and lands unattributed, exactly as it would have at
        // the time. A learner's batch is never attributed to somebody else.
        let token: string | null = null
        if (b.recordedLearnerId && b.recordedLearnerId === currentLearnerId
            && !b.recordedLearnerId.startsWith('guest-')) {
          await refreshToken()
          token = cachedToken
        }
        // A batch this log cannot attribute is LEFT for one that can (job
        // #504) — skipped, not stopped at, so it never blocks the batches
        // behind it.
        const decision = decide(b.recordedLearnerId, currentLearnerId, token)
        if (decision.hold) continue
        const outcome = await sendBatch(bodyFor(b), decision.token, true)
        // everything, this batch included, stays queued
        if (outcome === 'retry' || outcome === 'unauthorized') return
        removeBatch(b.id) // accepted, or refused on its merits: either way it is done
      }
      await reportDropped()
    } finally {
      replaying = false
    }
  }

  /**
   * The cap's cost, reported once rather than silently absorbed — and the
   * counter cleared only once the report has been ACCEPTED. #666 cleared it
   * first and then merely BUFFERED the report event, so a report lost to a
   * failed flush erased the only record that anything had been dropped.
   * `clearDropped` forgets exactly the number reported, so a drop that
   * happened while the report was in flight is still reported next time.
   */
  const reportDropped = async (): Promise<void> => {
    const dropped = peekDroppedCount()
    if (dropped <= 0) return
    const learnerId = resolveLearnerId()
    const body = bodyFor({
      events: [{
        client_event_id: genEventId(),
        event_type: 'telemetry_queue_dropped',
        payload: { events: dropped, ...(learnerId ? { learnerId } : {}) },
        course_code: resolveCourseCode(),
        session_id: sessionId,
        occurred_at: new Date().toISOString(),
        client_version: clientVersion,
      }],
      appShell: shellNow(),
      actingLearnerId: learnerId,
    })
    if (learnerId && !learnerId.startsWith('guest-')) await refreshToken()
    const decision = decide(learnerId, learnerId, cachedToken)
    if (decision.hold) return // reported next time, under a bearer
    const outcome = await sendBatch(body, decision.token, true)
    if (outcome === 'ok' || outcome === 'drop') clearDropped(dropped)
  }

  /** Drain the current buffer to the network. Silent on failure. */
  const flush = async (sync: boolean = false, cause: 'hidden' | 'unmount' = 'hidden'): Promise<void> => {
    if (buffer.length === 0) return
    // Don't fire a doomed request when we already know the network is gone.
    // On iOS every failed foreground request is a fresh chance to trip the
    // system "you're not connected — open Settings" alert Tom hit, and this
    // one flushes on a 5s interval, so an offline session fires it over and
    // over. Nothing is lost: the buffer is ALREADY dropped on failure by
    // design (see the header) — this drops it before touching the network
    // stack rather than after. isOfflineish() is the observed-stall signal,
    // never navigator.onLine on its own, so a weak-but-real connection still
    // flushes normally.
    if (isOfflineish()) {
      // The batch is no longer dropped here — it is PERSISTED and replayed
      // later, in order, with its original timestamps (job #666). The reason
      // not to touch the network stack is unchanged; only the fate of the
      // events is.
      for (const seg of segmentsOf(buffer.splice(0, buffer.length))) persistUnsent(seg)
      return
    }
    // View-as is READ-SHAPED (useViewAs.ts). Every request still carries the
    // ADMIN's own token, so a telemetry flush from a player mounted while an
    // ssi_admin is viewing-as writes player_events rows under the ADMIN's
    // learner — job #602 (staging, 2026-09-13) found infplay_enter /
    // cold_start / infplay_exit rows written this way. The fetch guard tags
    // the fetch path and the server refuses it, but sendBeacon never passes
    // through window.fetch, so the only structural stop is here, at the
    // source: while viewing-as, nothing leaves the buffer.
    if (useUserRole().isViewingAs.value) {
      buffer.length = 0 // dropped outright, never queued: not a learner's data
      return
    }
    const batch = buffer.splice(0, buffer.length)
    // ONE BATCH PER IDENTITY (job #671). The buffer can span a learner switch
    // — sign out, sign in as somebody else, five seconds — and a single body
    // carrying both would attribute all of it to whoever holds the bearer.
    const segments = segmentsOf(batch)

    // ORDER (spec 2). If anything is ALREADY queued, this batch is newer than
    // all of it, so it joins the back of the queue and the whole thing drains
    // oldest-first. Only the async path does this: the sync/unload path must
    // hand its payload to the browser while the page still exists, and cannot
    // spend that moment draining a queue.
    if (!sync && peekQueue().length > 0) {
      for (const seg of segments) persistUnsent(seg)
      await replayQueued()
      return
    }

    // `acting_learner_id` carries the play-as-class claim (owner ruling
    // 2026-07-16). It used to travel ONLY as the ssi-user-id cookie, which
    // does not cross an origin — inside a native shell the API is a different
    // origin, so class practice would silently attribute to the staff member
    // instead of the class. Sending it in the body works on both origins and
    // needs no cookie loosening. The server treats it as an unsigned CLAIM
    // exactly as it treats the cookie: honoured only for a class the verified
    // bearer may actually drive. Guest ids (`guest-<uuid>`) are not uuids and
    // are ignored server-side, so they are not worth sending. It is taken from
    // the segment's RECORDED identity, not from whoever is resolvable now.
    //
    // WHICH CONTAINER THIS LEARNER IS IN. `device_type` is derived server-side
    // from the user-agent and answers a different question — phone / tablet /
    // desktop — so a wrapped Android session and an ordinary phone browser both
    // read 'mobile' and are indistinguishable. For the India rollout, whose
    // whole question is whether the app beats the web, that distinction IS the
    // measurement. Sent once per batch (it is a property of the client, not of
    // an event) and read from the ONE platform door; the server falls back to
    // the user-agent's WebView marker if it is missing.

    // Attribution rides a VERIFIED bearer, never the cookie (SEC25 INPUT-04).
    // The token is cached from the previous flush so the unload path can use
    // it without awaiting; a background refresh keeps it current.
    //
    // The cache is ALSO primed at mount (see onMounted). Before that, the only
    // things that filled it were an async flush — timed, ten-event batch or
    // explicit — so any sync flush before the first of those had RESOLVED
    // beaconed its boot events with no bearer at all, and they landed
    // unattributed — nine such rows for class 8H, every one a cold_start /
    // bundle_boot_path / bundle_tier_heal at the head of its session (job
    // #307, 2026-09-12). Priming narrowed that window; the wait below
    // narrows it further (job #317), and job #320 states honestly what is
    // left of it.
    if (!sync) await refreshToken()
    let token = cachedToken
    if (sync && !token && options.getToken) {
      // The gap #307 left open (Astra, #316·G): priming at mount starts the
      // refresh, but a tab hidden or a player unmounted BEFORE getToken()
      // resolves still found the cache empty and beaconed the boot events
      // with no bearer. So a sync flush waits for the in-flight refresh
      // instead of racing it. A keepalive fetch issued after an awaited
      // promise inside a visibilitychange handler is still inside the
      // page's lifetime, on the same terms as sendBeacon. If no refresh is in
      // flight this starts one, so the NEXT sync path also has a token.
      //
      // HOW LONG TO WAIT is a trade-off the client cannot settle (Astra,
      // #319, on #317): a hidden page can be evicted at any moment without
      // firing anything, and a batch still held in JS at that moment is gone,
      // while a beacon already handed to the browser survives. Waiting longer
      // buys attribution and risks the batch; sending sooner secures the
      // batch and loses attribution. No client-only design closes both — a
      // full close needs an idempotent re-send the server can dedupe, which
      // is not built. So:
      //   • a page NOT at eviction risk — an unmount inside a live tab, or a
      //     tab that has come back to the foreground while we waited — keeps
      //     waiting, up to SAFE_TOKEN_WAIT_MS (a hung-client guard only);
      //   • a page that is hidden and STAYS hidden waits HIDDEN_TOKEN_WAIT_MS
      //     and then secures the batch as an unattributed beacon. A bearer
      //     that resolves after that point is NOT applied to this batch:
      //     that row lands unattributed, by this decision, and the next
      //     flush carries the token.
      const pending = refreshToken()
      const startedAt = Date.now()
      let settled = false
      void pending.then(() => { settled = true })
      while (!settled) {
        const atRisk = cause === 'hidden'
          && typeof document !== 'undefined' && document.visibilityState === 'hidden'
        const budget = atRisk ? HIDDEN_TOKEN_WAIT_MS : SAFE_TOKEN_WAIT_MS
        const remaining = budget - (Date.now() - startedAt)
        if (remaining <= 0) break
        let waitTimer: ReturnType<typeof setTimeout> | undefined
        try {
          await Promise.race([
            pending,
            new Promise<void>((r) => { waitTimer = setTimeout(r, Math.min(remaining, HIDDEN_TOKEN_WAIT_MS)) }),
          ])
        } finally {
          clearTimeout(waitTimer)
        }
      }
      token = cachedToken
    }

    const currentLearnerId = resolveLearnerId()
    for (const seg of segments) {
      const decision = decide(seg.recordedLearnerId, currentLearnerId, token)
      // Held (job #504): kept on the device and replayed under a bearer,
      // rather than filed anonymous now. A localStorage write is synchronous,
      // so the unload path can do this too.
      if (decision.hold) { persistUnsent(seg); continue }
      const segToken = decision.token
      const body = bodyFor({
        events: seg.events,
        appShell: shellNow(),
        actingLearnerId: seg.recordedLearnerId,
      })

      // sendBeacon for unmount/visibilitychange — survives page unload. It can't
      // carry a header, so it's only used when there's no token to carry (guest
      // sessions); a signed-in learner uses keepalive fetch instead, which the
      // same unload path supports.
      // sendBeacon returns false when the browser declines to queue the payload
      // — its per-origin beacon quota is full, or the page is being torn down —
      // and until job #320 that batch was simply dropped (Astra, #319). Now a
      // refused beacon falls through to the keepalive fetch below, the same
      // path a bearer-carrying sync flush already takes, so the batch is not
      // lost. A beacon that THROWS falls through the same way.
      if (sync && !segToken && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        try {
          const blob = new Blob([body], { type: 'application/json' })
          if (navigator.sendBeacon(apiUrl('/api/player-events'), blob)) continue
        } catch { /* fall through to fetch */ }
      }

      const outcome = await sendBatch(body, segToken, !sync)
      // A 401 means the server REFUSED the batch — it was not filed — so it is
      // queued on either path (job #504).
      if (outcome === 'unauthorized') { persistUnsent(seg); continue }
      // A SYNC flush has already handed its payload to a keepalive fetch and the
      // page may be gone before this resolves — and it would be persisting a
      // batch the browser is very likely to deliver anyway. So only the async
      // path queues on failure; the unload path is unchanged, and the
      // client_event_id makes the overlap between the two harmless.
      if (!sync && outcome === 'retry') persistUnsent(seg)
    }
  }

  // Flush on visibility change (tab hide) — important on iOS where
  // backgrounded tabs may be killed without unmount firing.
  const handleVisibilityChange = (): void => {
    if (typeof document === 'undefined') return
    if (document.visibilityState === 'hidden') {
      void flush(true, 'hidden')
    }
  }

  const live: LiveLog = { flush: () => flush(), pending: () => [...buffer] }

  /**
   * The 5s heartbeat. It used to be `flush()` alone, and flush's first line is
   * `if (buffer.length === 0) return` — so a learner who came back online and
   * then did NOTHING never replayed what was on the device: only a page load
   * or an `online` event would, and `online` does not fire when the tab was
   * already open through a silent recovery (job #671). Now an empty buffer
   * with a non-empty queue drains the queue instead. `peekQueue()` is a single
   * localStorage read and, once the queue is empty, this tick does nothing at
   * all. replayQueued carries its own isOfflineish and view-as gates.
   */
  const tick = async (): Promise<void> => {
    if (buffer.length > 0) { await flush(); return }
    if (peekQueue().length > 0) await replayQueued()
  }

  // The network coming back is the cheapest replay trigger there is: it fires
  // the moment the browser believes it can reach anything, long before the
  // next 5s flush would notice. isOfflineish() inside replayQueued is still
  // the gate, so a lying `online` event costs one refused request, not a loop.
  const handleOnline = (): void => { void replayQueued() }

  onMounted(() => {
    if (typeof window === 'undefined') return
    // Prime the bearer cache NOW, not on the first timed flush: the sync path
    // (tab hidden, unmount) cannot await and sends whatever is cached, so an
    // empty cache in the first seconds of a session is an unattributed row.
    void refreshToken()
    flushTimer = setInterval(() => { void tick() }, flushIntervalMs)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('online', handleOnline)
    liveLogs.add(live)
    // NEXT PAGE LOAD is the third replay trigger (spec 2): anything a previous
    // session left on the device goes out as soon as a player mounts.
    void replayQueued()
  })

  onBeforeUnmount(() => {
    liveLogs.delete(live)
    if (flushTimer) { clearInterval(flushTimer); flushTimer = null }
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', handleOnline)
    }
    // The tab is alive here — an unmount is a route change, not an unload —
    // so this flush may wait the full safe budget for its bearer.
    void flush(true, 'unmount')
  })

  return {
    /** Session id — same value across all events from this player mount. */
    sessionId,
    event,
    /** Force-flush now (e.g. before a known-suspicious operation). */
    flush,
    /** Drain the on-device queue now, oldest first. Silent on failure. */
    replayQueued,
  }
}
